// Custom 5-tier rank badges. Each rank has its own SVG with theme-appropriate gradient.
import { motion } from "framer-motion";

export const RANK_DEFS = {
    rookie: {
        key: "rookie",
        label: "Rookie",
        color: "#9ca3af",
        glow: "rgba(156,163,175,0.5)",
        gradient: ["#9ca3af", "#6b7280"],
    },
    hot: {
        key: "hot",
        label: "Hot",
        color: "#fb923c",
        glow: "rgba(249,115,22,0.55)",
        gradient: ["#fb923c", "#ef4444"],
    },
    heater: {
        key: "heater",
        label: "Heater",
        color: "#22d3ee",
        glow: "rgba(34,211,238,0.55)",
        gradient: ["#22d3ee", "#3b82f6"],
    },
    volcano: {
        key: "volcano",
        label: "Volcano",
        color: "#ef4444",
        glow: "rgba(239,68,68,0.6)",
        gradient: ["#ef4444", "#7f1d1d"],
    },
    goat: {
        key: "goat",
        label: "GOAT",
        color: "#facc15",
        glow: "rgba(250,204,21,0.65)",
        gradient: ["#facc15", "#a855f7", "#22d3ee"],
    },
};

function RookieIcon({ size }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
            <defs>
                <linearGradient id="r-rk" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#9ca3af" />
                    <stop offset="100%" stopColor="#4b5563" />
                </linearGradient>
            </defs>
            <path d="M12 2 L21 6 V12 C21 17 17 20.5 12 22 C7 20.5 3 17 3 12 V6 Z" fill="url(#r-rk)" stroke="#1f2937" strokeWidth="0.6" />
            <path d="M9.2 11 L11 13 L15 9" stroke="#fff" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function HotIcon({ size }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
            <defs>
                <radialGradient id="r-hot-bg" cx="50%" cy="60%" r="60%">
                    <stop offset="0%" stopColor="#fde047" />
                    <stop offset="60%" stopColor="#fb923c" />
                    <stop offset="100%" stopColor="#7c2d12" />
                </radialGradient>
            </defs>
            <path d="M12 2 C13 6 17 7 17 12 C17 16 14 19 12 19 C10 19 7 16 7 12 C7 9 9 8 9 5 C10 7 11 5 12 2 Z" fill="url(#r-hot-bg)" stroke="#7f1d1d" strokeWidth="0.5" />
            <path d="M11 12 C11.5 13.5 12.5 14 13 12.5 C13.3 11.7 12.7 11 12 10" fill="#fff" opacity="0.7" />
        </svg>
    );
}

function HeaterIcon({ size }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
            <defs>
                <linearGradient id="r-heat-bg" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#22d3ee" />
                    <stop offset="100%" stopColor="#1e40af" />
                </linearGradient>
            </defs>
            <path d="M13 2 L4 13 H10 L9 22 L20 10 H14 Z" fill="url(#r-heat-bg)" stroke="#0e7490" strokeWidth="0.6" />
            <path d="M13 4.5 L7 12 H10" stroke="#fff" strokeWidth="0.7" opacity="0.7" fill="none" />
        </svg>
    );
}

function VolcanoIcon({ size }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
            <defs>
                <linearGradient id="r-volc-bg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1f2937" />
                    <stop offset="60%" stopColor="#7f1d1d" />
                    <stop offset="100%" stopColor="#ef4444" />
                </linearGradient>
            </defs>
            <path d="M3 21 L21 21 L17 9 L13 11 L11 6 L8 12 Z" fill="url(#r-volc-bg)" stroke="#7f1d1d" strokeWidth="0.5" />
            <path d="M11 6 C12 4 13 5 13 7" stroke="#fde047" strokeWidth="0.9" fill="none" />
            <circle cx="11.5" cy="5" r="0.6" fill="#facc15" />
            <circle cx="13.6" cy="4.2" r="0.5" fill="#fb923c" />
            {/* lava drips */}
            <path d="M13 11 L12 13 L11 14" stroke="#facc15" strokeWidth="0.7" fill="none" />
            <path d="M17 9 L16 12" stroke="#facc15" strokeWidth="0.7" fill="none" />
        </svg>
    );
}

function GoatIcon({ size }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
            <defs>
                <linearGradient id="r-goat-bg" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#facc15" />
                    <stop offset="50%" stopColor="#fb923c" />
                    <stop offset="100%" stopColor="#a855f7" />
                </linearGradient>
                <linearGradient id="r-goat-gem" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#22d3ee" />
                    <stop offset="100%" stopColor="#a855f7" />
                </linearGradient>
            </defs>
            <path d="M3 18 L4 9 L8 13 L12 6 L16 13 L20 9 L21 18 Z" fill="url(#r-goat-bg)" stroke="#7c2d12" strokeWidth="0.6" />
            <rect x="3" y="18" width="18" height="3" fill="#7c2d12" />
            <circle cx="4" cy="9" r="1.2" fill="#22d3ee" />
            <circle cx="12" cy="6" r="1.6" fill="url(#r-goat-gem)" />
            <circle cx="20" cy="9" r="1.2" fill="#a855f7" />
            <circle cx="12" cy="19.5" r="0.8" fill="#facc15" />
        </svg>
    );
}

const ICON_MAP = {
    rookie: RookieIcon,
    hot: HotIcon,
    heater: HeaterIcon,
    volcano: VolcanoIcon,
    goat: GoatIcon,
};

export const RankBadge = ({ rank, size = "md", showLabel = true, animate = false }) => {
    if (!rank) return null;
    const def = RANK_DEFS[rank.key] || RANK_DEFS.rookie;
    const Icon = ICON_MAP[def.key] || RookieIcon;
    const sizes = {
        sm: { px: "px-1.5 py-0.5 text-[10px]", icon: 12 },
        md: { px: "px-2 py-0.5 text-xs", icon: 16 },
        lg: { px: "px-3 py-1.5 text-sm", icon: 22 },
        xl: { px: "px-4 py-2 text-base", icon: 36 },
    };
    const s = sizes[size] || sizes.md;
    const Component = animate ? motion.span : "span";
    const animateProps = animate
        ? {
              animate: { boxShadow: [`0 0 6px ${def.glow}`, `0 0 16px ${def.glow}`, `0 0 6px ${def.glow}`] },
              transition: { duration: 2.4, repeat: Infinity, ease: "easeInOut" },
          }
        : {};
    return (
        <Component
            {...animateProps}
            className={`inline-flex items-center gap-1 rounded-md border font-mono uppercase tracking-wider ${s.px}`}
            style={{
                color: def.color,
                borderColor: `${def.color}66`,
                background: `${def.color}14`,
                boxShadow: `0 0 8px ${def.glow}`,
            }}
            title={`${def.label} — streak ${rank.streak ?? 0}`}
            data-testid={`rank-badge-${def.key}`}
        >
            <Icon size={s.icon} />
            {showLabel && <span>{def.label}</span>}
        </Component>
    );
};

export const RankIcon = ({ rankKey, size = 24 }) => {
    const Icon = ICON_MAP[rankKey] || RookieIcon;
    return <Icon size={size} />;
};
